import { createContext, useContext, useMemo, type ReactNode } from "react";
import { StripeSdkFactory, type IStripeSdk } from "@repo/stripe-sdk";
import { useEnv } from "./env.context";

// StripeProvider builds the SDK lazily from the env config delivered by
// /api/env. The factory picks StripeSdkLocal (talks to stripe-sim) or
// StripeSdkReal (wraps @stripe/stripe-js) based on env.stripeMode — consumers
// get the same IStripeSdk contract either way.

const stripeContext = createContext<IStripeSdk | null>(null);

export function useStripe(): IStripeSdk {
  const ctx = useContext(stripeContext);
  if (!ctx) throw new Error("useStripe must be used within StripeProvider");
  return ctx;
}

export function StripeProvider({ children }: { children: ReactNode }) {
  const env = useEnv();
  // Memoise on the inputs the factory actually reads. In local mode that's
  // `stripeBaseUrl`; in prod it's `stripePublishableKey`. Other env changes
  // (which wouldn't happen at runtime anyway) won't recreate the SDK.
  const sdk = useMemo<IStripeSdk>(() => {
    if (env.stripeMode === "local") {
      return StripeSdkFactory.createInstance({
        mode: "local",
        baseUrl: env.stripeBaseUrl ?? "",
      });
    }
    return StripeSdkFactory.createInstance({
      mode: "prod",
      publishableKey: env.stripePublishableKey ?? "",
    });
  }, [env.stripeMode, env.stripeBaseUrl, env.stripePublishableKey]);

  return <stripeContext.Provider value={sdk}>{children}</stripeContext.Provider>;
}
